/**
 * Datos de la cuenta.
 *
 * La **etiqueta** es el campo que más importa acá: es lo que distingue dos
 * cuentas que comparten mesa en un restaurante donde las mesas no están
 * numeradas. "Camisa azul" le dice más al mesero que "Cuenta 2".
 *
 * Etiqueta y comensales viajan al backend (`PATCH /ordenes/:id`); el mesero a
 * cargo se queda local, porque es dato de sala y no afecta ni el total ni la
 * facturación.
 *
 * Desde acá también se mueve la cuenta de puesto: los clientes se cambian de
 * mesa, y sin esto la única salida sería cobrar y volver a tomar el pedido.
 */

import { useState } from 'react';
import { pad2 } from '../lib/formato';
import type { MesaVista } from '../datos/vistas';

interface Props {
  comensales: number;
  mesero: string;
  etiqueta: string | null;
  mesaActualId: string | null;
  mesasLibres: MesaVista[];
  onGuardar: (datos: {
    comensales: number;
    mesero: string;
    etiqueta: string | null;
    moverA: MesaVista | null;
  }) => void;
  onCerrar: () => void;
}

export function ModalDatosMesa({
  comensales,
  mesero,
  etiqueta,
  mesaActualId,
  mesasLibres,
  onGuardar,
  onCerrar,
}: Props) {
  const [pax, setPax] = useState(String(comensales));
  const [nombre, setNombre] = useState(mesero);
  const [nombreCuenta, setNombreCuenta] = useState(etiqueta ?? '');
  const [destino, setDestino] = useState('');

  const guardar = () =>
    onGuardar({
      comensales: Math.min(30, Math.max(1, Number(pax) || 1)),
      mesero: nombre.trim() || '—',
      etiqueta: nombreCuenta.trim() || null,
      moverA: destino ? (mesasLibres.find((m) => m.id === destino) ?? null) : null,
    });

  return (
    <div className="overlay on" onMouseDown={(e) => e.target === e.currentTarget && onCerrar()}>
      <div className="modal sm" role="dialog" aria-modal="true">
        <header>
          <h2>Datos de la cuenta</h2>
          <button className="close" onClick={onCerrar} aria-label="Cerrar">
            ✕
          </button>
        </header>

        <div className="body">
          <div className="field">
            <label htmlFor="etiquetaCuenta">Nombre de la cuenta</label>
            <input
              id="etiquetaCuenta"
              value={nombreCuenta}
              onChange={(e) => setNombreCuenta(e.target.value)}
              placeholder="Ej. Camisa azul, Cumpleaños de Ana"
            />
          </div>

          <div className="field">
            <label htmlFor="pax">Comensales</label>
            <input
              id="pax"
              type="number"
              min={1}
              max={30}
              value={pax}
              onChange={(e) => setPax(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="mesero">Mesero a cargo</label>
            <input
              id="mesero"
              type="text"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              placeholder="Nombre"
            />
          </div>

          {mesasLibres.length > 0 && (
            <div className="field" style={{ marginBottom: 0 }}>
              <label htmlFor="mover">Mover a otro puesto</label>
              <select
                id="mover"
                className="filter"
                style={{ marginBottom: 0 }}
                value={destino}
                onChange={(e) => setDestino(e.target.value)}
              >
                <option value="">Dejar donde está</option>
                {mesasLibres
                  .filter((m) => m.id !== mesaActualId)
                  .map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.nombre ?? `Mesa ${pad2(m.numero)}`}
                    </option>
                  ))}
              </select>
            </div>
          )}
        </div>

        <footer>
          <div style={{ flex: 1 }} />
          <button className="btn btn-primary" onClick={guardar}>
            Guardar
          </button>
        </footer>
      </div>
    </div>
  );
}
